import React from "react";
import {
  Autocomplete,
  Box,
  Button,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { FILTER_OPERATORS, getOperator } from "./explorerFilterUtils";

function newFilter() {
  return { id: `f_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`, field: "", op: "==", value: "", type: "string" };
}

function ValueInput({ filter, type, distinctValues, onChange }) {
  const operator = getOperator(filter.op);
  if (!operator.needsValue) {
    return (
      <TextField size="small" disabled value="—" sx={{ minWidth: 160 }} label="Value" />
    );
  }
  if (type === "boolean") {
    return (
      <TextField
        select
        size="small"
        label="Value"
        value={filter.value}
        onChange={(e) => onChange({ value: e.target.value })}
        sx={{ minWidth: 160 }}
      >
        <MenuItem value="true">true</MenuItem>
        <MenuItem value="false">false</MenuItem>
      </TextField>
    );
  }
  if (type === "timestamp") {
    return (
      <TextField
        type="date"
        size="small"
        label="Value"
        slotProps={{ inputLabel: { shrink: true } }}
        value={filter.value}
        onChange={(e) => onChange({ value: e.target.value })}
        sx={{ minWidth: 180 }}
      />
    );
  }
  if (type === "array" || operator.list) {
    return (
      <Autocomplete
        multiple
        freeSolo
        size="small"
        options={distinctValues || []}
        value={filter.value ? filter.value.split(",").map((s) => s.trim()).filter(Boolean) : []}
        onChange={(_, v) => onChange({ value: v.join(",") })}
        sx={{ minWidth: 220 }}
        renderInput={(params) => <TextField {...params} label="Values" placeholder="add" />}
      />
    );
  }
  return (
    <Autocomplete
      freeSolo
      size="small"
      options={distinctValues || []}
      value={filter.value || ""}
      onInputChange={(_, v) => onChange({ value: v })}
      sx={{ minWidth: 200 }}
      renderInput={(params) => <TextField {...params} label="Value" />}
    />
  );
}

/**
 * Field filter builder supporting unlimited AND filters with smart inputs.
 */
export default function ExplorerFilterBuilder({ filters, fieldNames, fieldTypes, distinctFor, onChange }) {
  const updateFilter = (id, patch) =>
    onChange(filters.map((f) => (f.id === id ? { ...f, ...patch } : f)));

  const addFilter = () => onChange([...filters, newFilter()]);
  const removeFilter = (id) => onChange(filters.filter((f) => f.id !== id));

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <FilterAltIcon fontSize="small" color="action" />
        <Typography variant="subtitle2">Field Filters</Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={addFilter}>
          Add filter
        </Button>
      </Stack>
      <Stack spacing={1}>
        {filters.length === 0 && (
          <Typography variant="caption" color="text.secondary">
            No field filters. Add one to build precise queries (contains, ==, ranges, in, array-contains…).
          </Typography>
        )}
        {filters.map((filter) => {
          const type = filter.field ? fieldTypes[filter.field] || "string" : "string";
          return (
            <Stack key={filter.id} direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Autocomplete
                size="small"
                options={fieldNames}
                value={filter.field || null}
                onChange={(_, v) => updateFilter(filter.id, { field: v || "", type: v ? fieldTypes[v] || "string" : "string" })}
                sx={{ minWidth: 200 }}
                renderInput={(params) => <TextField {...params} label="Field" />}
              />
              <TextField
                select
                size="small"
                label="Operator"
                value={filter.op}
                onChange={(e) => updateFilter(filter.id, { op: e.target.value })}
                sx={{ minWidth: 190 }}
              >
                {FILTER_OPERATORS.filter((o) => !o.textOnly || ["string", "reference", "map"].includes(type)).map((o) => (
                  <MenuItem key={o.id} value={o.id}>{o.label}</MenuItem>
                ))}
              </TextField>
              <ValueInput
                filter={filter}
                type={type}
                distinctValues={filter.field ? distinctFor(filter.field) : []}
                onChange={(patch) => updateFilter(filter.id, patch)}
              />
              {filter.field && (
                <Typography variant="caption" color="text.secondary">
                  {type}
                </Typography>
              )}
              <Tooltip title="Remove filter">
                <IconButton size="small" color="error" onClick={() => removeFilter(filter.id)}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          );
        })}
      </Stack>
    </Box>
  );
}
